import React, { useCallback, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import useQueryParams from 'hooks/useQueryParams'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { semiFormToObject } from 'lib/forms/formUtils'
import styled from 'styled-components'
import useToggle from 'hooks/useToggle'
import Group from 'components/utils/Group'
import FilterPanelCheckboxGroup, { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterSearchInput from 'components/Common/FilterPanel/FilterSearchInput'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import { rem } from 'polished'

import * as Analytics from 'analytics/analytics'

const CheckboxContainer = styled.div`
  padding-inline: ${rem(16)};
`

function buildPanelCheckItems(
  facets: Array<App.CruiseSearchFacet>,
  searchString?: string,
) {
  if (facets.length === 0) {
    return []
  }

  return facets.map((item): FilterPanelCheckItem => ({
    value: item.name,
    hidden: !!(searchString && facets.length > 5 && !item.name.toLowerCase().includes(searchString)),
    label: (
      <Group direction="horizontal" horizontalAlign="space-between">
        {item.name}
        <span>({item.count})</span>
      </Group>
    ),
  }))
}

const ShipFilterDropdown = styled.div`
  position: relative;
`

interface Props {
  listFilters: App.OfferListFilters
  onChange: (values: Record<string, any>) => void
}

function CruiseShipFilterDropdown(props: Props) {
  const { onChange, listFilters } = props
  const [open, toggleSortOpen, _, closeSort] = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const [cruiseShipSearch, setCruiseShipSearch] = useState<string>()
  const queryParams = useQueryParams()
  const containerRef = useRef<HTMLDivElement>(null)

  const facetParams = useMemo(() => ({
    facetTypes: ['ship_names'],
    cruiseLines: listFilters.cruiseLines,
    departurePlaceId: listFilters.departurePlaceId,
    rateCodes: listFilters.rateCodes,
    destinationId: listFilters.destinationId,
    destinationIds: listFilters.destinationIds,
    departureMonths: listFilters.departureMonths,
    durationMin: listFilters.durationMin,
    durationMax: listFilters.durationMax,
  } as App.CruiseSearchFacetParams), [listFilters])

  const [facets, fetchingFacets] = useCruiseSearchFacets(facetParams)
  const filters = useMemo(() => new Set(queryParams.getAll('cruiseShips')), [queryParams])

  const shipItems = useMemo(() => {
    const shipFacets = facets.filter(facet => facet.category === 'ship_names')
    return buildPanelCheckItems(shipFacets, cruiseShipSearch)
  }, [facets, cruiseShipSearch])

  const onFilterChange = useCallback(() => {
    const formValues = semiFormToObject<Record<string, any>>(containerRef.current!)
    onChange(formValues)
  }, [onChange])

  const onDelayedFilterChange = useMemo(() => debounce(onFilterChange, 500), [onFilterChange])

  return <ShipFilterDropdown ref={containerRef}>
    <AssistChip
      variant="filled"
      size="medium"
      onClick={() => {
        Analytics.trackClientEvent({
          subject: 'filter_opened',
          action: 'opened',
          category: 'cruise_ship_filter',
          type: 'interaction',
        })
        toggleSortOpen()
      }}
      startIcon={<LineShipIcon />}
      endIcon={<DropdownChevron open={open} />}
      ref={triggerRef}
    >
      Cruise ships
    </AssistChip>
    <DropdownList
      size="M"
      triggerRef={triggerRef}
      anchorRef={triggerRef}
      open={open}
      onClose={closeSort}
      placement="bottom-start"
    >
      <CheckboxContainer>
        {!fetchingFacets && <>
          {shipItems.length > 5 && <FilterSearchInput
            placeholder="Search cruise ships"
            onChange={setCruiseShipSearch}
            value={cruiseShipSearch}
          />}
          <FilterPanelCheckboxGroup
            name="cruiseShips"
            items={shipItems}
            onChange={onDelayedFilterChange}
            defaultSelected={filters}
            maxItems={5}
          />
        </>}
      </CheckboxContainer>
    </DropdownList>
  </ShipFilterDropdown>
}

export default CruiseShipFilterDropdown
